<script setup>
import { ref, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useTheme } from "../../composables/useTheme.js";
import { useSearch } from "../../composables/useSearch.js";
import { useTopBar } from "../../composables/useTopBar.js";
import { useShareModal } from "../../composables/useShareModal.js";
import { useDocumentDetails } from "../../composables/useDocumentDetails.js";
import DocumentDownloadMenu from "../ui/DocumentDownloadMenu.vue";

const props = defineProps({
  panelOpen: { type: Boolean, default: false },
  panelAvailable: { type: Boolean, default: false },
});
const emit = defineEmits(["toggle-panel"]);

const { isDark, toggleTheme } = useTheme();
const router = useRouter();
const route = useRoute();
const { inputValue, search, resetSearch } = useSearch();
const { title, subtitle, showMore, showShare, showDownload, canDownloadAnnotated, canDownloadOriginal } = useTopBar();
const { open: openShareModal } = useShareModal();
const { open: openDocumentDetails } = useDocumentDetails();

// The toggle button is `position: fixed` (see template) so it isn't clipped by
// the content column's `overflow-hidden` when it overlaps the sidebar/panel —
// but that means its `left` is viewport-relative, not header-relative, so we
// compute it from the known fixed widths (Sidebar.vue is w-20 = 80px; the
// panel is 256px per AppLayout.vue) instead of a Tailwind offset class.
function onMoreClick() {
  if (route.name === "v2-document") {
    openDocumentDetails({ documentId: route.params.docId, projectId: route.params.projectId });
  }
}

const searchInputEl = ref(null);
const isSearchRoute = () => route.name === "v2-search";

// Clear search state when leaving the search route
watch(() => route.name, (name) => {
  if (name !== "v2-search") resetSearch();
});

function onSearchFocus() {
  if (!isSearchRoute()) router.push({ name: "v2-search" });
}

function onSearchSubmit() {
  if (inputValue.value.trim()) search();
}

function onSearchClear() {
  resetSearch();
  searchInputEl.value?.focus();
}
</script>

<template>
  <header v-if="panelAvailable && !panelOpen"
    :class="['relative flex h-[52px] items-center gap-3 pr-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0', panelAvailable ? 'pl-14' : 'pl-4']">

    <!-- Toggle secondary panel — a half-circle handle flush against the exact
         seam where the panel/sidebar column ends and this content column
         begins. `fixed` (not `absolute`) so it isn't clipped by the content
         column's overflow-hidden when it overlaps the sidebar/panel, and
         paints above them regardless of DOM order. -->
    <button
      class="fixed top-[7px] left-[56px] flex items-center justify-center w-10 h-10 rounded-r-full border border-l-0 border-t-0 border-b-0 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md dark:shadow-black/40 [clip-path:inset(-12px_-12px_0px_0)] hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors z-40"
      :title="panelOpen ? 'Hide panel' : 'Show panel'" @click="emit('toggle-panel')">
      <svg v-if="!panelOpen" width="28" height="28" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="4" width="28" height="28" rx="14" fill="#E9F3FF" />
        <path
          d="M15.9317 11.334V24.4374M16.2811 18.0007C16.2811 18.0007 19.3635 18.0007 21.3385 18.0007M19.0397 20.0696C19.9374 19.2616 20.4408 18.8086 21.3385 18.0007C20.4408 17.1927 19.9374 16.7397 19.0397 15.9317M13.1731 24.6673H22.8282C23.8439 24.6673 24.6673 23.8439 24.6673 22.8282V13.1731C24.6673 12.1574 23.8439 11.334 22.8282 11.334H13.1731C12.1574 11.334 11.334 12.1574 11.334 13.1731V22.8282C11.334 23.8439 12.1574 24.6673 13.1731 24.6673Z"
          stroke="#2990FF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>

    </button>

    <!-- Back / Forward -->
    <div class="flex items-center gap-0.5 shrink-0">
      <button
        class="flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
        title="Go back" @click="router.go(-1)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </button>
      <button
        class="flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
        title="Go forward" @click="router.go(1)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </button>
    </div>

    <!-- ── Search route: centered search bar ───────────────────────────────── -->
    <template v-if="route.name === 'v2-search'">
      <div class="flex-1 flex justify-center">
        <div
          class="flex items-center w-full max-w-xl gap-2 px-4 py-1.5 rounded-full border border-blue-400 dark:border-blue-500 bg-white dark:bg-slate-900 shadow-[0_0_0_3px_rgba(96,165,250,0.15)] transition-all">
          <svg class="text-blue-400 shrink-0" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <input ref="searchInputEl" v-model="inputValue" type="text" placeholder="Search"
            class="flex-1 bg-transparent border-0 outline-none text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 min-w-0"
            @keydown.enter="onSearchSubmit" @keydown.escape="onSearchClear" />
          <button v-if="inputValue"
            class="flex items-center justify-center w-4 h-4 rounded-full bg-slate-300 dark:bg-slate-500 text-white border-0 cursor-pointer shrink-0 hover:bg-slate-400 transition-colors"
            @click="onSearchClear">
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Dark mode toggle (right, search route) -->
      <button
        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'" @click="toggleTheme">
        <svg v-if="isDark" width="15" height="15" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2" />
          <path
            d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </template>

    <!-- ── All other routes: page title + contextual actions ──────────────── -->
    <template v-else>
      <!-- Page title -->
      <div class="flex-1 flex items-center gap-2 min-w-0">
        <span v-if="title" class="text-[15px] font-bold text-slate-800 dark:text-slate-100 truncate">{{ title }}</span>
        <span v-if="subtitle" class="text-[15px] font-normal text-slate-400 dark:text-slate-500 truncate">{{ subtitle
        }}</span>
      </div>

      <!-- Right actions -->
      <div class="flex items-center gap-2 shrink-0">
        <!-- ⋮ more button -->
        <button v-if="showMore"
          class="flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          @click="onMoreClick">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>

        <!-- Download button (document viewer only) -->
        <DocumentDownloadMenu v-if="showDownload" :document-id="route.params.docId" :document-name="title"
          :can-download-annotated="canDownloadAnnotated" :can-download-original="canDownloadOriginal" />

        <!-- Share button -->
        <button v-if="showShare"
          class="flex items-center justify-center h-9 px-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          @click="openShareModal">
          Share
        </button>

        <!-- Dark mode toggle -->
        <button
          class="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'" @click="toggleTheme">
          <svg v-if="isDark" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2" />
            <path
              d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </template>

  </header>
</template>
